import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatusBadge from "@/components/shared/StatusBadge";
import SeverityBadge from "@/components/shared/SeverityBadge";
import MapView from "@/components/shared/MapView";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { downloadReport } from "@/lib/downloadReport";
import api, { extractApiError } from "@/lib/api";
import { useVehicleStateSocket } from "@/hooks/useVehicleStateSocket";
import { Activity, Loader2, Radio } from "lucide-react";

const ALLOWED_SEVERITIES = ["critical", "high", "medium", "low"];

function normalizeCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function readCoords(source) {
  if (!source) return null;
  if (Array.isArray(source) && source.length >= 2) {
    const lat = Number(source[0]);
    const lng = Number(source[1]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  }

  const lat = Number(source.lat ?? source.latitude);
  const lng = Number(source.lon ?? source.lng ?? source.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];

  if (Array.isArray(source.coordinates) && source.coordinates.length >= 2) {
    const cLat = Number(source.coordinates[0]);
    const cLng = Number(source.coordinates[1]);
    if (Number.isFinite(cLat) && Number.isFinite(cLng)) return [cLat, cLng];
  }
  return null;
}

function normalizeSeverity(value) {
  const candidate = String(value || "low").toLowerCase().trim();
  return ALLOWED_SEVERITIES.includes(candidate) ? candidate : "low";
}

function formatType(value) {
  return String(value || "Active Trip")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function extractVehicleId(payload) {
  if (!payload) return null;
  const value = payload.vehicle_id ?? payload.vehicleId;
  return value !== undefined && value !== null ? String(value) : null;
}

function mapTrip(item) {
  const tripId = item.id || item.trip_id;
  const vehicleId = item.vehicle?.id || item.vehicle_id;
  const pickupCoords = readCoords(item.pickup_location_obj || item.pickup || item.origin || item.start_location || item.location);
  const dropoffCoords = readCoords(item.dropoff_location_obj || item.dropoff || item.destination || item.end_location);
  return {
    id: String(tripId || ""),
    trip_id: String(tripId || ""),
    status: String(item.status || "ACTIVE"),
    type: formatType(item.trip_type || item.incident_type || item.type),
    severity: normalizeSeverity(item.severity || item.priority),
    started_at: item.started_at || item.start_time,
    updated_at: item.updated_at || item.last_updated,
    eta_minutes: item.eta_minutes || item.eta_min || null,
    pickup_location: item.pickup_location || item.origin_address || item.location || "Unknown pickup",
    dropoff_location: item.dropoff_location || item.destination_address || "Unknown drop-off",
    pickup_coords: pickupCoords,
    dropoff_coords: dropoffCoords,
    vehicle_id: vehicleId ? String(vehicleId) : null,
    vehicle_plate: item.vehicle?.plate || item.vehicle?.registration_number || item.vehicle_plate || "Unknown",
    vehicle_name: item.vehicle?.name || [item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(" ") || "Ambulance",
    driver_name: item.driver?.name || item.driver_name || "Unknown",
  };
}

function mapTelemetry(item) {
  const coords = readCoords(item?.location || item);
  const speedRaw = item?.gps_speed ?? item?.speed ?? item?.speed_kmh;
  const speed = parseFloat(speedRaw);
  return {
    coords,
    speed: Number.isFinite(speed) ? speed : null,
    last_updated: item?.last_updated || item?.timestamp || item?.created_at || null,
    status: item?.status ? String(item.status).toLowerCase() : null,
  };
}

export default function AmbulanceActive() {
  const location = useLocation();
  const navigate = useNavigate();

  const passedId = location.state?.selectedId ? String(location.state.selectedId) : null;

  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState({});

  const [selectedId, setSelectedId] = useState(null);
  const [telemetryByVehicle, setTelemetryByVehicle] = useState({});
  const [seedingTelemetry, setSeedingTelemetry] = useState(false);

  const vehicleIdsRef = useRef(new Set());

  const selected = useMemo(() => trips.find((t) => t.trip_id === selectedId) || null, [trips, selectedId]);

  const activeVehicleIds = useMemo(
    () => Array.from(new Set(trips.map((t) => t.vehicle_id).filter(Boolean).map(String))),
    [trips],
  );

  useEffect(() => {
    vehicleIdsRef.current = new Set(activeVehicleIds);
  }, [activeVehicleIds]);

  const fetchTrips = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get("/trips", {
        params: { status: "ACTIVE", page, page_size: pageSize },
      });

      const rows = normalizeCollection(response.data).map(mapTrip).filter((t) => t.trip_id);
      setTrips(rows);
      setMeta(response.data?.meta || {});

      setSelectedId((prevSelectedId) => {
        if (passedId && rows.some((t) => t.trip_id === passedId)) {
          return passedId;
        }
        const selectedExists = rows.some((t) => t.trip_id === prevSelectedId);
        return selectedExists ? prevSelectedId : (rows[0]?.trip_id || null);
      });
    } catch (err) {
      setError(extractApiError(err).message || "Failed to load active trips");
      toast.error("Failed to load active trips");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, passedId]);

  const seedTelemetry = useCallback(async (rows) => {
    const vehicleIds = Array.from(new Set(rows.map((t) => t.vehicle_id).filter(Boolean)));
    if (!vehicleIds.length) return;

    try {
      setSeedingTelemetry(true);
      const results = await Promise.all(
        vehicleIds.map(async (vehicleId) => {
          try {
            const response = await api.get(`/vehicles/${vehicleId}/telemetry/latest`);
            return { vehicleId: String(vehicleId), telemetry: mapTelemetry(response.data) };
          } catch {
            return { vehicleId: String(vehicleId), telemetry: null };
          }
        }),
      );

      setTelemetryByVehicle((prev) => {
        const next = { ...prev };
        results.forEach(({ vehicleId, telemetry }) => {
          if (!telemetry) return;
          next[vehicleId] = {
            ...(next[vehicleId] || {}),
            ...telemetry,
          };
        });
        return next;
      });
    } finally {
      setSeedingTelemetry(false);
    }
  }, []);

  useEffect(() => {
    fetchTrips();
  }, [fetchTrips]);

  useEffect(() => {
    if (trips.length) seedTelemetry(trips);
  }, [trips, seedTelemetry]);

  useEffect(() => {
    if (!passedId) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [passedId, navigate, location.pathname]);

  useVehicleStateSocket((payload) => {
    const vehicleId = extractVehicleId(payload);
    if (!vehicleId || !vehicleIdsRef.current.has(vehicleId)) return;

    const patch = mapTelemetry(payload);
    setTelemetryByVehicle((prev) => ({
      ...prev,
      [vehicleId]: {
        ...(prev[vehicleId] || {}),
        ...(patch.coords ? { coords: patch.coords } : {}),
        ...(patch.speed !== null ? { speed: patch.speed } : {}),
        ...(patch.last_updated ? { last_updated: patch.last_updated } : {}),
        ...(patch.status ? { status: patch.status } : {}),
      },
    }));
  });

  const handleDownload = useCallback(() => {
    downloadReport({
      filename: "ambulance_active_trips",
      title: "Ambulance - Active Trips Report",
      headers: ["Vehicle", "Driver", "Status", "Current Speed (km/h)", "Pickup", "Drop-off"],
      rows: trips.map((t) => {
        const liveKey = t.vehicle_id ? String(t.vehicle_id) : null;
        const live = liveKey ? telemetryByVehicle[liveKey] : null;
        return [
          `${t.vehicle_plate} (${t.vehicle_name})`,
          t.driver_name,
          t.status,
          live?.speed ?? "-",
          t.pickup_location,
          t.dropoff_location,
        ];
      }),
    });
    toast.success("Report downloaded");
  }, [trips, telemetryByVehicle]);

  const mapMarkers = useMemo(() => {
    if (!selected) return [];
    const liveKey = selected.vehicle_id ? String(selected.vehicle_id) : null;
    const live = liveKey ? telemetryByVehicle[liveKey] : null;
    const liveCoords = live?.coords;

    return [
      liveCoords ? {
        coords: liveCoords,
        title: selected.vehicle_plate,
        subtitle: Number.isFinite(Number(live?.speed)) ? `Speed ${Number(live.speed).toFixed(1)} km/h` : "Live position",
        severity: "low",
        type: "ambulance",
      } : null,
      selected.pickup_coords ? {
        coords: selected.pickup_coords,
        title: "Pickup",
        subtitle: selected.pickup_location,
        severity: selected.severity,
        type: "patient",
      } : null,
      selected.dropoff_coords ? {
        coords: selected.dropoff_coords,
        title: "Hospital",
        subtitle: selected.dropoff_location,
        severity: "low",
        type: "hospital",
      } : null,
    ].filter(Boolean);
  }, [selected, telemetryByVehicle]);

  const mapCenter = useMemo(() => {
    if (!selected) return [19.076, 72.8777];
    const liveKey = selected.vehicle_id ? String(selected.vehicle_id) : null;
    const live = liveKey ? telemetryByVehicle[liveKey] : null;
    return live?.coords || selected.pickup_coords || selected.dropoff_coords || [19.076, 72.8777];
  }, [selected, telemetryByVehicle]);

  const pageCount = Math.max(1, Math.ceil((meta.total || 0) / pageSize));
  const hasNext = meta.has_next ?? (trips.length >= pageSize);

  return (
    <div className="space-y-6" data-testid="ambulance-active">
      <PageHeader
        title="Active Trips"
        subtitle="Live patient transports your crew is handling."
        onDownload={handleDownload}
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-3">
          <SectionCard>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Trip feed</p>
                <p className="text-sm text-slate-700">Status: ACTIVE</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600">
                <Radio className="h-4 w-4 text-emerald-600" />
                {seedingTelemetry ? "Syncing telemetry..." : "Live updates on"}
              </div>
            </div>
          </SectionCard>

          {loading ? (
            <SectionCard>
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            </SectionCard>
          ) : null}

          {!loading && error ? (
            <SectionCard>
              <p className="text-sm text-rose-600">{error}</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={fetchTrips}>Retry</Button>
            </SectionCard>
          ) : null}

          {!loading && !error && trips.length === 0 ? (
            <SectionCard>
              <p className="text-sm text-slate-500">No active trips.</p>
            </SectionCard>
          ) : null}

          {!loading && !error && trips.map((t) => {
            const liveKey = t.vehicle_id ? String(t.vehicle_id) : null;
            const live = liveKey ? telemetryByVehicle[liveKey] : null;
            return (
              <button
                key={t.trip_id}
                onClick={() => setSelectedId(t.trip_id)}
                data-testid={`trip-${t.trip_id}`}
                className={`w-full rounded-xl border bg-white p-4 text-left transition ${selectedId === t.trip_id ? "border-[hsl(var(--role-primary))] shadow-md" : "border-slate-100 hover:border-slate-200"}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[11px] font-semibold uppercase tracking-widest text-slate-400">{t.vehicle_plate}</p>
                  <SeverityBadge severity={t.severity} />
                </div>
                <p className="mt-1 font-semibold text-slate-900">{t.type}</p>
                <p className="text-xs text-slate-500">{t.pickup_location}</p>
                <div className="mt-2 flex items-center justify-between">
                  <StatusBadge status={String(t.status).toLowerCase()} />
                  <span className="text-xs text-slate-500">
                    {Number.isFinite(Number(live?.speed)) ? `${Number(live.speed).toFixed(1)} km/h` : "Speed —"}
                  </span>
                </div>
              </button>
            );
          })}

          <SectionCard>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-slate-600">Page {page} of {pageCount}</p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={loading || page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                <Button size="sm" variant="outline" disabled={loading || !hasNext} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4 xl:col-span-2">
          {selected ? (
            <>
              <SectionCard padded={false}>
                <div className="p-3">
                  <MapView
                    markers={mapMarkers}
                    center={mapCenter}
                    zoom={14}
                    height={340}
                    testId="ambulance-map"
                  />
                </div>
              </SectionCard>

              <SectionCard title="Trip details" subtitle={selected.vehicle_plate}>
                <div className="grid grid-cols-2 gap-3 text-xs md:grid-cols-3">
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Vehicle</p>
                    <p className="mt-0.5 font-mono text-[11px] font-semibold tracking-widest text-slate-900">{selected.vehicle_plate}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Driver</p>
                    <p className="mt-0.5 font-semibold text-slate-900">{selected.driver_name}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Current Speed</p>
                    <p className="mt-0.5 flex items-center gap-1 font-semibold text-slate-900">
                      <Activity size={13} className="text-emerald-600" />
                      {selected.vehicle_id && Number.isFinite(Number(telemetryByVehicle[String(selected.vehicle_id)]?.speed))
                        ? `${Number(telemetryByVehicle[String(selected.vehicle_id)].speed).toFixed(1)} km/h`
                        : "—"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Pickup</p>
                    <p className="mt-0.5 font-semibold text-slate-900">{selected.pickup_location}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Drop-off</p>
                    <p className="mt-0.5 font-semibold text-slate-900">{selected.dropoff_location}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Last Telemetry</p>
                    <p className="mt-0.5 font-semibold text-slate-900">
                      {selected.vehicle_id ? formatTime(telemetryByVehicle[String(selected.vehicle_id)]?.last_updated) : "—"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Started At</p>
                    <p className="mt-0.5 font-semibold text-slate-900">{formatTime(selected.started_at)}</p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">ETA</p>
                    <p className="mt-0.5 font-semibold text-slate-900">
                      {selected.eta_minutes ? `${selected.eta_minutes} min` : "—"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3">
                    <p className="text-slate-500">Live Status</p>
                    <p className="mt-0.5 font-semibold text-slate-900">
                      {selected.vehicle_id ? telemetryByVehicle[String(selected.vehicle_id)]?.status || "online" : "—"}
                    </p>
                  </div>
                </div>
              </SectionCard>
            </>
          ) : (
            <SectionCard>
              <p className="text-sm text-slate-500">Select a trip from the list.</p>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
